/**
 * E2E renderer probe — drives the `studio_link_failed` writer for the
 * ENG-4483 service-named refusals (capacity exhausted / capacity unknown /
 * capability unavailable) via `globalThis.__irisStudioLinkFailedTelemetry`.
 *
 * WHY: the e2e account runs against the deployed ITG agent-registry, which
 * cannot be put at Desktop capacity or have its add capability toggled from a
 * test, so the picker never receives one of these refusals live. This probe
 * builds the SAME error shape `wrapApiCall` throws (ApiError whose cause is
 * the decoded api.v1.Error carrying a marshaled AgentRegistrySetupFailure),
 * runs it through the SAME classifier the picker uses (`setupErrorType`) and
 * the SAME typed tracker (`createStudioBootstrapTracker`), so the receipt
 * (posthog-studio-link-failed.spec.ts) proves the envelope decode, the
 * classification and the send boundary end to end. Jest pins the classifier
 * table; the receipt pins that the name and `error_type` leave the app.
 *
 * Same IRIS_E2E_TEST gate as registration-telemetry-probe.ts. No product
 * behaviour. Per rule: stay-in-scope (ai.knowledge/global).
 * Per knowledge: posthog-ci-receipt-selection (ai.knowledge/platform) — the
 * receipt this probe feeds is how posthog-selection picks the event.
 */
import {
  AgentRegistrySetupFailure,
  AgentRegistrySetupReason
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { createStudioBootstrapTracker } from '#~/analytics/domains/studio-bootstrap.js';
import { EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { ApiError } from '#~/api/types.js';
import { setupErrorType } from '#~/routes/select-studio/setup-error-type.js';

/** The service reasons ENG-4483 W2/W3 introduced, by the picker's analytics name. */
export type ProbedSetupRefusal =
  | 'capacity_exhausted'
  | 'capacity_unknown'
  | 'capability_unavailable';

const REASON_BY_NAME: Record<
  ProbedSetupRefusal,
  { reason: AgentRegistrySetupReason; code: number }
> = {
  capacity_exhausted: {
    reason: AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPACITY_EXHAUSTED,
    code: 409
  },
  capacity_unknown: {
    reason: AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPACITY_UNKNOWN,
    code: 503
  },
  capability_unavailable: {
    reason: AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPABILITY_UNAVAILABLE,
    code: 400
  }
};

interface StudioLinkFailedTelemetryProbe {
  /** Emit one `studio_link_failed` classified from a refusal envelope of the given kind. */
  failed(refusal: ProbedSetupRefusal): void;
}

/**
 * Renderer-process gate. `process.env` is NOT populated in this renderer even
 * with nodeIntegration — the preload bridge is the only place the inherited OS
 * environment is visible (see calibration-probe.ts for the full note).
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/** The exact error `wrapApiCall` raises when the registry refuses a link with a setup reason. */
export const refusalError = (refusal: ProbedSetupRefusal): ApiError => {
  const { reason, code } = REASON_BY_NAME[refusal];
  return new ApiError('link refused', {
    code,
    cause: {
      code,
      message: 'link refused',
      context: AgentRegistrySetupFailure.encode(
        AgentRegistrySetupFailure.fromPartial({ reason, correlationId: 'e2e-probe' })
      ).finish()
    }
  });
};

/**
 * Drive the real classifier and tracker. Exported so the unit test can pin that
 * the probe does not hard-code `error_type` — a probe that bypassed
 * `setupErrorType` would receipt a mapping the picker never runs.
 */
export function driveStudioLinkFailed(
  refusal: ProbedSetupRefusal,
  track: Parameters<typeof createStudioBootstrapTracker>[0]
): void {
  const analytics = createStudioBootstrapTracker(track);
  // Property shape mirrors the picker's add-path failure emit: bounded, PII-free
  // (see StudioBootstrapProperties). Values are representative, not observed.
  analytics.track(EVENT_NAMES.STUDIO_LINK_FAILED, {
    surface: 'electron_account_bootstrap',
    picker_mode: 'bridge_setup',
    local_bridge_state: 'unlinked',
    studio_count: 1,
    target_link_status: 'linked',
    action: 'add',
    outcome: 'failure',
    error_type: setupErrorType(refusalError(refusal))
  });
}

if (isE2eEnabled()) {
  const probe: StudioLinkFailedTelemetryProbe = {
    failed(refusal: ProbedSetupRefusal): void {
      driveStudioLinkFailed(refusal, (name, category, properties) => {
        void trackAnalytics({ category, name, properties } as AnalyticsEvent);
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisStudioLinkFailedTelemetry = probe;
}
